import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import toast from 'components/partials/toast/toast';

import { getCalculationsTableSettings } from '../calculations-step-table-settings';
import {
  AWARDING_LOGIC_DICTIONARY_TYPE,
  DEFAULT_SPECIAL_AWARD_AMOUNT,
  ELIGIBILITY_CRITERIA_DICTIONARY_TYPE,
  ELIGIBILITY_CRITERIA_OPTIONS,
  ELIGIBILITY_OPERAND_DICTIONARY_TYPE,
  ELIGIBILITY_OPERAND_LOCAL_LABELS,
  ELIGIBILITY_OPERAND_OPTIONS,
  GIFT_TARGET_DICTIONARY_TYPE,
  GIFT_TARGET_OPTIONS,
  GRANT_CAP_DICTIONARY_TYPE,
  GRANT_CAP_OPTIONS,
  VARIANT_SECTION_VISIBILITY,
} from '../calculations-step.constants';
import { buildDictionaryOptions } from '../helpers/build-dictionary-options';
import type { DictionaryDrivenOption } from '../helpers/build-dictionary-options';
import { resolveCalculationVariant } from '../helpers/resolve-calculation-variant';
import type { RegimeDefinitionLike } from '../helpers/resolve-calculation-variant';
import { ruleToRequest } from '../helpers/special-award-row';
import { splitStoredCeebCodes } from '../helpers/ceeb-codes';
import { useCalculationDraft } from './use-calculation-draft';
import type { UseCalculationDraftResult } from './use-calculation-draft';
import type {
  SpecialAwardLogicCategory,
  SpecialAwardRegime,
} from './use-special-award-form';

import {
  useAcademicRanks,
  useCustomLogic,
  useRegimes,
} from '../../../hooks/use-aid-regimes';
import {
  useCalculation,
  useCalculationRules,
  useCeebCodesValidationQueries,
  useCreateCalculation,
  useDeleteCalculationRule,
  useInvalidateCeebCodesValidation,
  useUpdateCalculation,
  useUpdateCalculationRule,
} from '../../../hooks/use-aid-calculations';
import type { CeebCodesValidationItem } from '../../../hooks/use-aid-calculations';
import { useInvalidateAidResults } from '../../../hooks/use-aid-results';

import { useCurrentUser } from 'hooks/api/use-current-user';
import { useDictionaryByType } from 'hooks/use-dictionary-by-type';
import { useDictionaryOptions } from 'hooks/use-dictionary-options';

import type { SpecialAwardChipContext } from '../helpers/special-award-row';
import type {
  CalculationGiftTargetType,
  CalculationGrantCap,
  CalculationSectionVisibility,
  CalculationSettingsDraft,
  SpecialAwardRow,
} from '../calculations-step.types';
import {
  AidModelCalculationRequestCalculationGiftTargetType,
  AidModelCalculationRequestGrantCap,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';
import type {
  AcademicRankDto,
  AidModelCalculationDto,
  AidModelCalculationRequest,
  CalculationRuleRequest,
} from 'api/generated/schemas';

interface UseCalculationsStepParams {
  clientId?: string | number;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface UseCalculationsStepResult {
  modelId: string | undefined;
  userId: string | undefined;
  clientId: string | number | undefined;
  calculation: AidModelCalculationDto | undefined;
  settingsToRequest: () => AidModelCalculationRequest;
  settings: CalculationSettingsDraft;
  visibility: CalculationSectionVisibility;
  disabledGiftTargets: ReadonlySet<CalculationGiftTargetType>;
  giftTargetOptions: DictionaryDrivenOption<CalculationGiftTargetType>[];
  grantCapOptions: DictionaryDrivenOption<CalculationGrantCap>[];
  setGiftTarget: UseCalculationDraftResult['setGiftTarget'];
  setIncludeRequiredFees: UseCalculationDraftResult['setIncludeRequiredFees'];
  setAdjustmentLoans: UseCalculationDraftResult['setAdjustmentLoans'];
  setAdjustmentWorkStudy: UseCalculationDraftResult['setAdjustmentWorkStudy'];
  setCalculateNewNeed: UseCalculationDraftResult['setCalculateNewNeed'];
  setGrantCap: UseCalculationDraftResult['setGrantCap'];
  setGrantCapAmount: UseCalculationDraftResult['setGrantCapAmount'];
  rules: SpecialAwardRow[];
  rulesLoading: boolean;
  columns: ReturnType<typeof getCalculationsTableSettings>;
  isInitialLoading: boolean;
  isSaving: boolean;
  hasChanges: boolean;
  grantCapAmountError: boolean;
  reset: () => void;
  save: () => Promise<void>;
  isAwardModalOpen: boolean;
  /** Rule the modal is editing; `undefined` when it is adding a new award. */
  editingRule: SpecialAwardRow | undefined;
  onOpenAddAward: () => void;
  onCloseAwardModal: () => void;
  /** Refreshes Results / Awarding summary after a special award is added or edited. */
  onAwardSuccess: () => void;
}

export const useCalculationsStep = ({
  clientId: clientIdProp,
  onDirtyChange,
}: UseCalculationsStepParams): UseCalculationsStepResult => {
  const { clientId: clientIdParam, modelId } = useParams();
  const clientId = clientIdProp ?? clientIdParam;
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId;

  const [isSaving, setIsSaving] = useState(false);
  const [grantCapAmountError, setGrantCapAmountError] = useState(false);
  const [isAwardModalOpen, setIsAwardModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<SpecialAwardRow | undefined>();

  const regimesQuery = useRegimes({
    userId: userId ?? '',
    modelId: modelId ?? '',
    enabled: !!userId && !!modelId,
  });
  const regimesList = (regimesQuery.data ?? []) as RegimeDefinitionLike[];

  const firstRegime = regimesList[0];

  const variant = useMemo(
    () => resolveCalculationVariant(firstRegime),
    [firstRegime]
  );
  const visibility = VARIANT_SECTION_VISIBILITY[variant];

  const disabledGiftTargets =
    useMemo((): ReadonlySet<CalculationGiftTargetType> => {
      if (firstRegime?.awardType === 'AGI') {
        return new Set([
          AidModelCalculationRequestCalculationGiftTargetType.COA,
          AidModelCalculationRequestCalculationGiftTargetType.DIRECT,
        ]);
      }

      return new Set();
    }, [firstRegime?.awardType]);

  const calculationQuery = useCalculation({
    userId: userId ?? '',
    modelId: modelId ?? '',
    enabled: !!userId && !!modelId,
  });
  const calculation = calculationQuery.data;

  const createCalculation = useCreateCalculation();
  const updateCalculation = useUpdateCalculation();
  const updateRule = useUpdateCalculationRule();
  const deleteRule = useDeleteCalculationRule();
  const invalidateAidResults = useInvalidateAidResults();

  const calculationId = calculation?.id;

  const rulesQuery = useCalculationRules({
    userId: userId ?? '',
    modelId: modelId ?? '',
    calculationId: calculationId ?? '',
    enabled: !!userId && !!modelId && calculationId != null,
  });
  const rules = rulesQuery.data ?? [];

  const regimesForSummary = useMemo(
    () => (regimesQuery.data ?? []) as SpecialAwardRegime[],
    [regimesQuery.data]
  );

  const customLogicQuery = useCustomLogic({
    userId: userId ?? '',
    clientId: clientId ?? '',
    enabled: !!userId && !!clientId,
  });
  const categories = useMemo(
    () => (customLogicQuery.data ?? []) as SpecialAwardLogicCategory[],
    [customLogicQuery.data]
  );

  const academicRanksQuery = useAcademicRanks({
    userId: userId ?? '',
    aidModelId: modelId ?? '',
    enabled: !!userId && !!modelId,
  });

  const { options: awardLogicOptions, isLoading: isAwardLogicLoading } =
    useDictionaryOptions({
      userId: userId ?? '',
      type: AWARDING_LOGIC_DICTIONARY_TYPE,
      enabled: !!userId,
    });

  const eligibilityCriteriaDictionaryQuery = useDictionaryByType(
    userId ?? '',
    ELIGIBILITY_CRITERIA_DICTIONARY_TYPE,
    { enabled: !!userId }
  );

  const eligibilityOperandDictionaryQuery = useDictionaryByType(
    userId ?? '',
    ELIGIBILITY_OPERAND_DICTIONARY_TYPE,
    { enabled: !!userId }
  );

  // FR-29/FR-30: the backend doesn't persist per-code match status, so the
  // table re-derives it the same way the modal does — one validation query per
  // distinct CEEB `values` csv among the loaded rules.
  const ceebValidationItems = useMemo<CeebCodesValidationItem[]>(() => {
    const seen = new Set<string>();

    return rules.reduce<CeebCodesValidationItem[]>((items, rule) => {
      const ceebCriteria = rule.eligibilityCriterias?.find(
        (criteria) => criteria.operand === EligibilityCriteriaDtoOperand.IN
      );
      const values = ceebCriteria?.values ?? '';

      if (!values || seen.has(values)) {
        return items;
      }

      seen.add(values);
      items.push({ id: values, ceebCodes: splitStoredCeebCodes(values) });

      return items;
    }, []);
  }, [rules]);

  const ceebValidationResults = useCeebCodesValidationQueries(
    userId ?? '',
    modelId ?? '',
    ceebValidationItems
  );

  const invalidateCeebCodesValidation = useInvalidateCeebCodesValidation();
  const hasRevalidatedCeebOnEntry = useRef(false);

  // Bypasses the query client's 30s staleTime so returning to this page
  // always re-checks CEEB codes, instead of showing a stale cached result.
  useEffect(() => {
    if (
      hasRevalidatedCeebOnEntry.current ||
      !userId ||
      !modelId ||
      !ceebValidationItems.length
    ) {
      return;
    }

    hasRevalidatedCeebOnEntry.current = true;
    invalidateCeebCodesValidation(userId, modelId);
  }, [userId, modelId, ceebValidationItems, invalidateCeebCodesValidation]);

  const ceebMatchSummaries = useMemo(
    () =>
      Object.fromEntries(
        ceebValidationItems.flatMap((item, index) => {
          const data = ceebValidationResults[index]?.data;

          if (!data) {
            return [];
          }

          const validSet = new Set(data.valid ?? []);
          const matched = item.ceebCodes.filter((code) =>
            validSet.has(code)
          ).length;

          return [[item.id, { matched, total: item.ceebCodes.length }]];
        })
      ),
    [ceebValidationItems, ceebValidationResults]
  );

  const chipContext = useMemo<SpecialAwardChipContext>(
    () => ({
      regimes: regimesForSummary,
      categories,
      criteriaLabels: Object.fromEntries(
        buildDictionaryOptions(
          ELIGIBILITY_CRITERIA_OPTIONS,
          eligibilityCriteriaDictionaryQuery.data
        ).map((option) => [option.value, option.label])
      ),
      operandLabels: Object.fromEntries(
        buildDictionaryOptions(
          ELIGIBILITY_OPERAND_OPTIONS,
          eligibilityOperandDictionaryQuery.data,
          ELIGIBILITY_OPERAND_LOCAL_LABELS
        ).map((option) => [option.value, option.label])
      ),
      rankLabels: Object.fromEntries(
        ((academicRanksQuery.data ?? []) as AcademicRankDto[])
          .filter((rank) => rank.id != null)
          .map((rank) => [String(rank.id), String(rank.rank)])
      ),
      ceebMatchSummaries,
    }),
    [
      regimesForSummary,
      categories,
      eligibilityCriteriaDictionaryQuery.data,
      eligibilityOperandDictionaryQuery.data,
      academicRanksQuery.data,
      ceebMatchSummaries,
    ]
  );

  const giftTargetDictionaryQuery = useDictionaryByType(
    userId ?? '',
    GIFT_TARGET_DICTIONARY_TYPE,
    { enabled: !!userId }
  );
  const giftTargetLocalWins = useMemo(
    () =>
      new Set([AidModelCalculationRequestCalculationGiftTargetType.TUITION]),
    []
  );

  const giftTargetOptions = useMemo(
    () =>
      buildDictionaryOptions(
        GIFT_TARGET_OPTIONS,
        giftTargetDictionaryQuery.data,
        giftTargetLocalWins
      ),
    [giftTargetDictionaryQuery.data, giftTargetLocalWins]
  );

  const grantCapDictionaryQuery = useDictionaryByType(
    userId ?? '',
    GRANT_CAP_DICTIONARY_TYPE,
    { enabled: !!userId }
  );
  const grantCapOptions = useMemo(
    () =>
      buildDictionaryOptions(GRANT_CAP_OPTIONS, grantCapDictionaryQuery.data),
    [grantCapDictionaryQuery.data]
  );

  const isInitialLoading =
    regimesQuery.isLoading ||
    calculationQuery.isLoading ||
    giftTargetDictionaryQuery.isLoading ||
    grantCapDictionaryQuery.isLoading ||
    isAwardLogicLoading;

  const {
    settings,
    hasChanges,
    setGiftTarget,
    setIncludeRequiredFees,
    setAdjustmentLoans,
    setAdjustmentWorkStudy,
    setCalculateNewNeed,
    setGrantCap,
    setGrantCapAmount,
    reset,
    markForReinit,
    toRequest,
  } = useCalculationDraft(calculation);

  useEffect(() => {
    const fallback =
      AidModelCalculationRequestCalculationGiftTargetType.TUITION;

    if (
      disabledGiftTargets.size > 0 &&
      disabledGiftTargets.has(settings.calculationGiftTargetType) &&
      !disabledGiftTargets.has(fallback)
    ) {
      setGiftTarget(fallback);
    }
  }, [disabledGiftTargets, settings.calculationGiftTargetType, setGiftTarget]);

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  useEffect(() => {
    setGrantCapAmountError(false);
  }, [settings.grantCapAmount, settings.grantCap]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!hasChanges || !userId || !modelId) {
      return;
    }

    const requiresGrantCapAmount =
      settings.grantCap !== AidModelCalculationRequestGrantCap.NO_GRANT_CAP;

    if (requiresGrantCapAmount && settings.grantCapAmount == null) {
      setGrantCapAmountError(true);

      toast.error({
        title: 'Grant cap amount is required',
        message: 'Please enter the grant cap amount before saving',
      });

      return;
    }

    setIsSaving(true);

    try {
      const data = toRequest();
      const existingId = calculation?.id;

      if (existingId != null) {
        await updateCalculation.mutateAsync({
          userId,
          modelId,
          id: existingId,
          data,
        });
      } else {
        await createCalculation.mutateAsync({ userId, modelId, data });
      }

      await invalidateAidResults(userId, modelId);

      markForReinit();

      toast.success({
        title: 'Calculation updated',
        message: 'Calculation settings have been saved successfully',
      });
    } catch (error) {
      const err = error as { data?: { detail?: string } };

      toast.error({
        title: 'Error saving calculation',
        message: err?.data?.detail || 'An error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    hasChanges,
    userId,
    modelId,
    settings.grantCap,
    settings.grantCapAmount,
    toRequest,
    calculation?.id,
    createCalculation,
    updateCalculation,
    invalidateAidResults,
    markForReinit,
  ]);

  const handleOpenAddAward = useCallback(() => {
    setEditingRule(undefined);
    setIsAwardModalOpen(true);
  }, []);

  const handleEditRule = useCallback((rule: SpecialAwardRow) => {
    setEditingRule(rule);
    setIsAwardModalOpen(true);
  }, []);

  const handleCloseAwardModal = useCallback(() => {
    setIsAwardModalOpen(false);
    setEditingRule(undefined);
  }, []);

  const handleAwardSuccess = useCallback(() => {
    if (!userId || !modelId) return;
    void invalidateAidResults(userId, modelId);
  }, [userId, modelId, invalidateAidResults]);

  const rulesMutating = updateRule.isPending || deleteRule.isPending;

  const handleMutationError = useCallback((error: unknown): void => {
    const err = error as { data?: { detail?: string } };

    toast.error({
      title: 'Error updating special award',
      message: err?.data?.detail || 'An error occurred',
    });
  }, []);

  const handleDeleteRule = useCallback(
    (rule: SpecialAwardRow): void => {
      if (!userId || !modelId || calculationId == null || rule.id == null) {
        return;
      }

      deleteRule.mutate(
        { userId, modelId, calculationId, ruleId: rule.id },
        { onError: handleMutationError, onSuccess: handleAwardSuccess }
      );
    },
    [
      userId,
      modelId,
      calculationId,
      deleteRule,
      handleMutationError,
      handleAwardSuccess,
    ]
  );

  const handleAmountCommit = useCallback(
    (rule: SpecialAwardRow, amount: number | null): void => {
      if (!userId || !modelId || calculationId == null || rule.id == null) {
        return;
      }

      if ((rule.awardAmount ?? null) === amount) {
        return;
      }

      updateRule.mutate(
        {
          userId,
          modelId,
          calculationId,
          ruleId: rule.id,
          data: {
            ...ruleToRequest(rule),
            awardAmount: amount ?? DEFAULT_SPECIAL_AWARD_AMOUNT,
          },
        },
        { onError: handleMutationError, onSuccess: handleAwardSuccess }
      );
    },
    [
      userId,
      modelId,
      calculationId,
      updateRule,
      handleMutationError,
      handleAwardSuccess,
    ]
  );

  const handleLogicChange = useCallback(
    (rule: SpecialAwardRow, logic: string): void => {
      if (!userId || !modelId || calculationId == null || rule.id == null) {
        return;
      }

      if (rule.awardLogic === logic) {
        return;
      }

      updateRule.mutate(
        {
          userId,
          modelId,
          calculationId,
          ruleId: rule.id,
          data: {
            ...ruleToRequest(rule),
            awardLogic: logic as CalculationRuleRequest['awardLogic'],
          },
        },
        { onError: handleMutationError, onSuccess: handleAwardSuccess }
      );
    },
    [
      userId,
      modelId,
      calculationId,
      updateRule,
      handleMutationError,
      handleAwardSuccess,
    ]
  );

  const columns = useMemo(
    () =>
      getCalculationsTableSettings({
        chipContext,
        awardLogicOptions,
        onEdit: handleEditRule,
        onDelete: handleDeleteRule,
        onAmountCommit: handleAmountCommit,
        onLogicChange: handleLogicChange,
      }),
    [
      chipContext,
      awardLogicOptions,
      handleEditRule,
      handleDeleteRule,
      handleAmountCommit,
      handleLogicChange,
    ]
  );

  return {
    modelId,
    userId,
    clientId,
    calculation,
    settingsToRequest: toRequest,
    settings,
    visibility,
    disabledGiftTargets,
    giftTargetOptions,
    grantCapOptions,
    setGiftTarget,
    setIncludeRequiredFees,
    setAdjustmentLoans,
    setAdjustmentWorkStudy,
    setCalculateNewNeed,
    setGrantCap,
    setGrantCapAmount,
    rules,
    rulesLoading: rulesQuery.isFetching || rulesMutating,
    columns,
    isInitialLoading,
    isSaving,
    hasChanges,
    grantCapAmountError,
    reset,
    save: handleSave,
    isAwardModalOpen,
    editingRule,
    onOpenAddAward: handleOpenAddAward,
    onCloseAwardModal: handleCloseAwardModal,
    onAwardSuccess: handleAwardSuccess,
  };
};
